import { useId, useState } from 'react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { MemberSignInChange, MemberSignInState } from './memberSignInTypes'

export function MemberSignInDialog({
  kind,
  state,
  pending,
  error,
  onClose,
  onSave,
}: {
  kind: 'secret' | 'link'
  state: MemberSignInState
  pending: boolean
  error: string | null
  onClose: () => void
  onSave: (change: MemberSignInChange) => void
}) {
  const [confirmed, setConfirmed] = useState(false)
  const [draft, setDraft] = useState(state.stored_link_address ?? '')
  const id = useId()
  const secret = kind === 'secret'
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !pending) onClose()
      }}
    >
      <DialogContent
        closeDisabled={pending}
        {...(secret ? {} : { 'aria-describedby': undefined })}
        onEscapeKeyDown={(event) => {
          if (pending) event.preventDefault()
        }}
        onInteractOutside={(event) => {
          if (pending) event.preventDefault()
        }}
      >
        <DialogHeader>
          <DialogTitle>{secret ? 'Issue new secret?' : 'Link address'}</DialogTitle>
          {secret && <DialogDescription>{state.confirmation.description}</DialogDescription>}
        </DialogHeader>
        {secret ? (
          <>
            <div className="flex items-center gap-2 text-body-sm">
              <Checkbox
                id={id}
                checked={confirmed}
                disabled={pending}
                onCheckedChange={(value) => setConfirmed(value === true)}
              />
              <label htmlFor={id}>{state.confirmation.checkbox_label}</label>
            </div>
            {error && <Alert className="mt-2">{error}</Alert>}
          </>
        ) : (
          <LinkField
            id={id}
            value={draft}
            hint={state.link_address_hint}
            error={error}
            pending={pending}
            onChange={setDraft}
          />
        )}
        <DialogFooter className={secret ? undefined : 'pt-4'}>
          <Button variant="outline" disabled={pending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={secret ? 'destructive' : 'default'}
            disabled={pending || (secret && !confirmed)}
            onClick={() => onSave(secret ? { confirmed: true } : { link_address: draft })}
          >
            {secret ? 'Issue new secret' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function LinkField({
  id,
  value,
  hint,
  error,
  pending,
  onChange,
}: {
  id: string
  value: string
  hint: string
  error: string | null
  pending: boolean
  onChange: (value: string) => void
}) {
  return (
    <div>
      <Input
        aria-label="Link address"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error ${id}-hint` : `${id}-hint`}
        value={value}
        disabled={pending}
        onChange={(event) => onChange(event.target.value)}
      />
      {error && (
        <p id={`${id}-error`} role="alert" className="pt-2 text-caption text-destructive">
          {error}
        </p>
      )}
      <p id={`${id}-hint`} className="pt-2 text-caption text-muted-foreground">
        {hint}
      </p>
    </div>
  )
}
